import { useUploadFiles } from '@skeepers/upload-core';
import PropTypes from 'prop-types';
import React, { createContext, useState, useCallback, useEffect } from 'react';
import { v4 as uuid } from 'uuid';

import { transformStringToIdIfIsBase64 } from '@app/utils/formats';
import { saveLocalValues, getLocalValue, removeLocalValue, findLocalStorageValues, regexKey } from '@app/utils/localstorage';
import { useCreateParticipationInput } from '@hooks/useCreateParticipationInput';
import { useDeleteParticipationInput } from '@hooks/useDeleteParticipationInput';
import { useUploadFile } from '@hooks/useUploadFile';

export const UploadContext = createContext();

// TODO
// - document.addEventListener('online'/'offline');

/**
 * IDB Upload provider
 */
export const UploadProvider = ({ children, brief = {} }) => {
  const [uploadFile] = useUploadFile();
  const [createParticipationInput, { loading: createParticipationInputLoading }] = useCreateParticipationInput();
  const [deleteParticipationInput] = useDeleteParticipationInput();

  const [online, setOnline] = useState(navigator.onLine);
  const [inputs, setInputs] = useState([]);
  const [files, setFiles] = useState([]);
  const [participation, setParticipation] = useState(null);

  const uploadFilesResult = useUploadFiles(files, {
    onEdit: (newValue) => {
      setFiles(newValue);
    },
    onFileUploaded: async (file) => {
      saveLocalValues(file.key, { status: 'PENDING_SUBMISSION' });

      const localStorageData = getLocalValue(file.key);
      await submitInput({ key: file.key, ...localStorageData });
    },
    getSession: async (file) => {
      if (file.session) {
        return file.session;
      }

      const result = await uploadFile({
        type: 'UPLOADER',
        origin: window.location.origin,
        contentType: file.blob.type,
        filename: file.blob.name,
      });

      saveLocalValues(file.key, result.data?.uploadFile);
      return result.data?.uploadFile.session;
    },
  });
  const { progress, addFiles, deleteAllFiles } = uploadFilesResult[1];

  /**
   * At the end of this method, if the createParticipationInput works,
   * we must clean the blob in the indexDB => the input is attached to the participation and the file is uploaded.
   */
  const submitInput = useCallback(async (input) => {
    if (createParticipationInputLoading) {
      return;
    }

    const { data, errors } = await createParticipationInput({
      participationId: input.participationId,
      sequenceId: input.sequenceId,
      url: input.url,
      filename: input.filename || input?.file?.name,
    });

    if (errors?.length) {
      console.error('Error while creating participation input', errors[0].message);
      saveLocalValues(input.key, {
        status: 'FAILED',
      });
    } else {
      saveLocalValues(input.key, {
        status: 'PUSHED',
        inputId: data.createParticipationInput.participationInput.id,
        uploaded: true,
      });
    }

    mapInputs();
  }, [createParticipationInput, createParticipationInputLoading]);

  const deleteInput = useCallback(async (input) => {
    if (input.inputId) {
      await deleteParticipationInput({
        id: input.inputId,
      });
    }
  }, [deleteParticipationInput]);

  const uploadInput = useCallback(
    (input) => {
      return addFiles([
        {
          key: input.key,
          blob: input.file,
          fileName: input.file.name,
          session: input.session,
        },
      ])[0];
    },
    [addFiles]
  );

  const mapInputs = () => {
    const inputs = [];
    const keys = findLocalStorageValues(regexKey);
    for (const key of keys) {
      inputs.push(getLocalValue(key));
    }
    setInputs(inputs);
  }

  const resetFileUploaded = (inputKey) => {
    deleteAllFiles();
    saveLocalValues(inputKey, { uploaded: undefined });
    mapInputs();
  };

  /**
   * First clean the local DB to avoid more than 1 input on the same sequence
   * Then add and save the input
   */
  const add = (sequenceId, customMetadata = {}) => {
    const toRemoveFromLocalStorage = findLocalStorageValues(regexKey);
    const toRemove = toRemoveFromLocalStorage.filter((key) => getLocalValue(key).sequenceId === sequenceId);
    toRemove.forEach((key) => removeLocalValue(key));

    const key = uuid();
    saveLocalValues(key, {
      key,
      participationId: participation.id,
      sequenceId,
      status: 'PENDING_VALIDATION',
      createdAt: Math.round(Date.now() / 1000),
      ...customMetadata,
    });
    mapInputs();
    return key;
  };

  const removeFromKey = async (key, permanent = false) => {
    removeLocalValue(key);
    if (permanent) {
      const input = inputs.find((i) => i.key === key);
      await deleteInput(input);
    }
  };

  const removeFromKeys = async (keys, permanent = false) => {
    await Promise.all(keys.map((key) => removeFromKey(key, permanent)));
  };

  /**
   * Validate and start uploading right away
   */
  const validate = async (input, participationInformation) => {
    const uploadId = await uploadInput(input);
    const payload = {
      status: 'PENDING_UPLOAD',
      uploadId,
      ...participationInformation
    }
    saveLocalValues(input.key, payload);
    mapInputs();
  };

  // Watch connectitity
  const onOnline = useCallback(() => setOnline(true), []);
  const onOffline = useCallback(() => setOnline(false), []);

  const setParticipationInformation = (p) => {
    setParticipation(p);
    if (p && p.inputs?.length > 0) {
      const sequencesInTemplate = brief?.timelineTemplates?.length ? brief.timelineTemplates[0].scheme?.mainTimeline?.map((element) => element.sequence.id) : [];

      const { inputs = [], projects = [] } = p;
      // these inputs are uploaded (submitted) to a video project.
      // for uploaded inputs we display green arrow icon (no more action from user).
      const uploadedInputs = projects.length
        ? [
          ...projects[0].inputs.map(({ participationInputId }) => participationInputId),
          // TODO: remove this line after evolution in https://skeepers.atlassian.net/browse/UGV-2782
          // This is an edge case.
          // There is possibility that a sequence is not used in timelineTemplate.
          // In this case video for this sequence won't be uploaded to a project (we ignore it).
          // But user has sent it, so we need to display green arrow as well.
          ...(sequencesInTemplate.length
            ? inputs.filter((input) => !sequencesInTemplate.includes(input.sequence.id)).map(({ id }) => transformStringToIdIfIsBase64(id))
            : []),
        ]
        : [];

      [...inputs]
        .sort((a, b) => b.createdAt - a.createdAt)
        .reduce((acc, input) => {
          const x = acc.find(item => item.sequence.id === input.sequence.id);
          if (!x) {
              return acc.concat([input]);
          }
          return acc;
        }, [])
        .filter((input) => {
          const localValues = findLocalStorageValues(/^su-[-0-9a-f]+/);
          return !localValues.find((key) => {
            const value = getLocalValue(key);
            return value.participationId === p.id && value.sequenceId === input.sequence.id;
          });
        })
        .forEach((input) => {
          const key = uuid();

          saveLocalValues(key, {
            key,
            participationId: p.id,
            sequenceId: input.sequence.id,
            status: uploadedInputs.includes(transformStringToIdIfIsBase64(input.id)) ? 'SUBMITTED' : 'PUSHED',
            inputId: input.id,
            url: input.url,
            createdAt: input.createdAt,
            filename: input.filename,
          });
        });

        if (inputs.length > 0) {
          mapInputs();
        }
    }
  }

  useEffect(() => {
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [onOnline, onOffline]);

  return (
    <UploadContext.Provider
      value={{
        remainingTime: progress.eta,
        progress: progress,
        inputs,
        online,
        add,
        validate,
        removeFromKeys,
        resetFileUploaded,
        setParticipationInformation,
        participation,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
};

UploadProvider.propTypes = {
  children: PropTypes.node.isRequired,
  brief: PropTypes.shape({
    timelineTemplates: PropTypes.arrayOf(
      PropTypes.shape({
        scheme: PropTypes.shape({
          mainTimeline: PropTypes.arrayOf(
            PropTypes.shape({
              sequence: PropTypes.shape({
                id: PropTypes.string,
              }),
            })
          ),
        }),
      })
    ),
  }).isRequired,
};
